import React, { useMemo } from 'react';
import {
  CustomUiFileItem,
  getApiBotProviderCustomUiGetFileContent,
  usePostApiBotProviderCustomUiUploadFile,
  useDeleteApiBotProviderCustomUiDeleteFile,
  usePostApiBotProviderCustomUiCreateFolder,
  useDeleteApiBotProviderCustomUiDeleteFolder,
  usePostApiBotProviderCustomUiSaveFile,
  usePostApiBotProviderCustomUiRenameFile,
  usePostApiBotProviderCustomUiDuplicateFile,
  usePostApiBotProviderCustomUiRenameFolder,
  usePostApiBotProviderCustomUiDuplicateFolder,
} from '@/API';
import { GetWebBotCustomUiUrl, GetBotProviderCustomUiDownloadZipUrl } from '@/APIURL';
import {
  VirtualFileExplorerModal,
  type VirtualFileExplorerAdapter,
} from '@/Components/VirtualFileExplorer';

export interface CustomUiExplorerModalProps {
  opened: boolean;
  onClose: () => void;
  workspaceId: string;
  botProviderId: string;
  files: CustomUiFileItem[];
  initialSelectedFile?: string | null;
  onFilesUpdated?: () => void;
}

export function CustomUiExplorerModal({
  opened,
  onClose,
  workspaceId,
  botProviderId,
  files,
  initialSelectedFile = null,
  onFilesUpdated,
}: CustomUiExplorerModalProps) {
  const uploadFileMutation = usePostApiBotProviderCustomUiUploadFile();
  const deleteFileMutation = useDeleteApiBotProviderCustomUiDeleteFile();
  const createFolderMutation = usePostApiBotProviderCustomUiCreateFolder();
  const deleteFolderMutation = useDeleteApiBotProviderCustomUiDeleteFolder();
  const saveFileMutation = usePostApiBotProviderCustomUiSaveFile();
  const renameFileMutation = usePostApiBotProviderCustomUiRenameFile();
  const duplicateFileMutation = usePostApiBotProviderCustomUiDuplicateFile();
  const renameFolderMutation = usePostApiBotProviderCustomUiRenameFolder();
  const duplicateFolderMutation = usePostApiBotProviderCustomUiDuplicateFolder();

  const isOperating =
    uploadFileMutation.isPending ||
    deleteFileMutation.isPending ||
    createFolderMutation.isPending ||
    deleteFolderMutation.isPending ||
    saveFileMutation.isPending ||
    renameFileMutation.isPending ||
    duplicateFileMutation.isPending ||
    renameFolderMutation.isPending ||
    duplicateFolderMutation.isPending;

  const adapter: VirtualFileExplorerAdapter = useMemo(() => {
    return {
      isOperating,
      getFileContent: async (filePath: string) => {
        const res = await getApiBotProviderCustomUiGetFileContent({
          workspaceId,
          botProviderId,
          filePath,
        });
        return typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2);
      },
      saveFile: async (filePath: string, content: string) => {
        await saveFileMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { filePath, content },
        });
      },
      uploadFile: async (relativePath: string, file: File) => {
        await uploadFileMutation.mutateAsync({
          params: { workspaceId, botProviderId, relativePath },
          data: { file },
        });
      },
      deleteFile: async (filePath: string) => {
        await deleteFileMutation.mutateAsync({
          params: { workspaceId, botProviderId, filePath },
        });
      },
      renameFile: async (oldFilePath: string, newFileName: string) => {
        await renameFileMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { oldFilePath, newFileName },
        });
      },
      duplicateFile: async (sourceFilePath: string, targetFileName: string) => {
        await duplicateFileMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { sourceFilePath, targetFileName },
        });
      },
      createFolder: async (folderPath: string) => {
        await createFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { folderPath },
        });
      },
      deleteFolder: async (folderPath: string) => {
        await deleteFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId, folderPath },
        });
      },
      renameFolder: async (oldFolderPath: string, newFolderName: string) => {
        await renameFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { oldFolderPath, newFolderName },
        });
      },
      duplicateFolder: async (sourceFolderPath: string, targetFolderName: string) => {
        await duplicateFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { sourceFolderPath, targetFolderName },
        });
      },
      getFileUrl: (filePath: string) => GetWebBotCustomUiUrl(botProviderId, filePath),
      getDownloadZipUrl: () => GetBotProviderCustomUiDownloadZipUrl(workspaceId, botProviderId),
      downloadZipFileName: `custom-ui-${botProviderId}.zip`,
    };
  }, [
    workspaceId,
    botProviderId,
    isOperating,
    saveFileMutation,
    uploadFileMutation,
    deleteFileMutation,
    renameFileMutation,
    duplicateFileMutation,
    createFolderMutation,
    deleteFolderMutation,
    renameFolderMutation,
    duplicateFolderMutation,
  ]);

  const filePaths = useMemo(() => {
    return (files || []).map((f) => f.filePath || '').filter(Boolean);
  }, [files]);

  return (
    <VirtualFileExplorerModal
      opened={opened}
      onClose={onClose}
      title="自訂 UI 檔案總管"
      rootLabel="自訂 UI 根目錄"
      files={filePaths}
      initialSelectedFile={initialSelectedFile}
      adapter={adapter}
      onFilesUpdated={onFilesUpdated}
    />
  );
}
